import { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export function UnsubscribeModal({ open, onOpenChange }) {
    const [contact, setContact] = useState("");
    const [channel, setChannel] = useState("both");
    const [status, setStatus] = useState(null); // {ok, msg}
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        try {
            const { data } = await api.post("/unsubscribe", { contact, channel });
            // Only show success if the server confirms success
            if (data.success) {
                setStatus({ ok: true, msg: data.message });
            } else {
                setStatus({ ok: false, msg: "Unable to unsubscribe. Please check your details." });
            }
        } catch (err) {
            setStatus({ ok: false, msg: formatApiError(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setContact(""); setChannel("both"); setStatus(null);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
            <DialogContent
                className="border border-line bg-surface rounded-2xl shadow-lift sm:max-w-md"
                data-testid="unsubscribe-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl font-bold tracking-[-0.02em]">Unsubscribe</DialogTitle>
                    <DialogDescription className="font-sans text-sm text-dim leading-relaxed">
                        Enter the email or phone number on your account and choose what to stop receiving.
                    </DialogDescription>
                </DialogHeader>

                {status?.ok ? (
                    <div
                        className="flex items-start gap-3 rounded-xl border border-pos/30 bg-pos/[0.07] p-4"
                        data-testid="unsubscribe-success"
                    >
                        <CheckCircle2 className="text-pos shrink-0 mt-0.5" size={18} />
                        <p className="text-sm text-dim">{status.msg}</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <label className="eyebrow text-dim block mb-2">Email or phone</label>
                            <input
                                required
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                placeholder="you@example.com or +1 555 123 4567"
                                data-testid="unsubscribe-contact"
                                className="field"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-dim block mb-2">Unsubscribe from</label>
                            <div className="grid grid-cols-3 gap-2 p-1 rounded-xl bg-surface-sunken border border-line">
                                {["email", "sms", "both"].map((c) => (
                                    <button
                                        type="button"
                                        key={c}
                                        onClick={() => setChannel(c)}
                                        data-testid={`unsubscribe-channel-${c}`}
                                        className={`py-2 rounded-lg font-mono text-[11px] uppercase tracking-wider transition-colors duration-150 ${
                                            channel === c
                                                ? "bg-accent text-accent-foreground font-semibold"
                                                : "text-dim hover:text-foreground hover:bg-surface-raised"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {status && !status.ok && (
                            <div
                                className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3"
                                data-testid="unsubscribe-error"
                            >
                                <AlertCircle className="text-neg shrink-0 mt-0.5" size={16} />
                                <p className="text-sm text-neg">{status.msg}</p>
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="unsubscribe-submit"
                            className="w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-110 hover:shadow-glow transition-[filter,box-shadow] duration-150 disabled:opacity-50 disabled:hover:shadow-none"
                        >
                            {loading ? "Processing..." : "Unsubscribe"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
